import React, { useState, useEffect, useMemo } from 'react';
import { deviceAPI, assignmentAPI, usersAPI, transferAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import EditDeviceDialog from './EditDeviceDialog';
import './DeviceList.css';

const DeviceList = () => {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Helper function to get user-specific localStorage key
  const getStorageKey = (key) => {
    return user?.email ? `deviceList_${user.email}_${key}` : `deviceList_${key}`;
  };

  // Helper function to load from localStorage
  const loadFromStorage = (key, defaultValue) => {
    try {
      const stored = localStorage.getItem(getStorageKey(key));
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error(`Error loading ${key} from localStorage:`, error);
      return defaultValue;
    }
  };

  // Helper function to save to localStorage
  const saveToStorage = (key, value) => {
    try {
      localStorage.setItem(getStorageKey(key), JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving ${key} to localStorage:`, error);
    }
  };
  
  // Column filters - load from localStorage
  const [columnFilters, setColumnFilters] = useState(() => 
    loadFromStorage('columnFilters', {
      assetId: '',
      deviceName: '',
      deviceType: '',
      model: '',
      osVersion: '',
      condition: '',
      status: '',
      assignedTo: '',
      deliveryUnit: ''
    })
  );
  
  // Sorting state - load from localStorage
  const [sortColumn, setSortColumn] = useState(() => 
    loadFromStorage('sortColumn', 'assetId')
  );
  const [sortDirection, setSortDirection] = useState(() => 
    loadFromStorage('sortDirection', 'desc')
  );
  
  // Search tooltip state
  const [activeSearchColumn, setActiveSearchColumn] = useState(null);

  // Custom filter state - load from localStorage
  const [customFilter, setCustomFilter] = useState(() => 
    loadFromStorage('customFilter', {
      field: '',
      value: ''
    })
  );

  // Global search state - load from localStorage
  const [globalSearch, setGlobalSearch] = useState(() => 
    loadFromStorage('globalSearch', '')
  );

  // Assignee filter state - load from localStorage
  const [assigneeFilter, setAssigneeFilter] = useState(() => 
    loadFromStorage('assigneeFilter', '')
  );

  // Quick filters for status and condition - load from localStorage
  const [showAvailableOnly, setShowAvailableOnly] = useState(() => 
    loadFromStorage('showAvailableOnly', false)
  );
  const [showGoodOnly, setShowGoodOnly] = useState(() => 
    loadFromStorage('showGoodOnly', true)
  );

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deviceToEdit, setDeviceToEdit] = useState(null);

  // View details dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deviceToView, setDeviceToView] = useState(null);

  // Reassign dialog state
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [deviceToReassign, setDeviceToReassign] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [reassignNotes, setReassignNotes] = useState('');
  const [isReassigning, setIsReassigning] = useState(false);

  // Infinite scroll state
  const [displayedCount, setDisplayedCount] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const itemsPerPage = 20;

  // Export/Import menu state
  const [menuOpen, setMenuOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importing, setImporting] = useState(false);

  // Error state
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && !event.target.closest('button')) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (user?.email) {
      saveToStorage('columnFilters', columnFilters);
    }
  }, [columnFilters, user?.email]);

  useEffect(() => {
    if (user?.email) {
      saveToStorage('sortColumn', sortColumn);
    }
  }, [sortColumn, user?.email]);

  useEffect(() => {
    if (user?.email) {
      saveToStorage('sortDirection', sortDirection);
    }
  }, [sortDirection, user?.email]);

  useEffect(() => {
    if (user?.email) {
      saveToStorage('customFilter', customFilter);
    }
  }, [customFilter, user?.email]);

  useEffect(() => {
    if (user?.email) {
      saveToStorage('globalSearch', globalSearch);
    }
  }, [globalSearch, user?.email]);

  useEffect(() => {
    if (user?.email) {
      saveToStorage('assigneeFilter', assigneeFilter);
    }
  }, [assigneeFilter, user?.email]);

  useEffect(() => {
    if (user?.email) {
      saveToStorage('showAvailableOnly', showAvailableOnly);
    }
  }, [showAvailableOnly, user?.email]);

  useEffect(() => {
    if (user?.email) {
      saveToStorage('showGoodOnly', showGoodOnly);
    }
  }, [showGoodOnly, user?.email]);

  const loadData = async () => {
    try {
      setLoadError(null);
      
      console.log('DeviceList - User info:', { 
        role: user?.role, 
        deliveryUnit: user?.deliveryUnit,
        email: user?.email 
      });
      
      const [devicesRes, assignmentsRes, usersRes] = await Promise.all([
        deviceAPI.getAll(),
        assignmentAPI.getAll(),
        usersAPI.getDeviceManagers()
      ]);
      
      console.log(`DeviceList - Loaded ${devicesRes.data.length} devices for ${user?.role}`);
      
      setDevices(devicesRes.data);
      setAssignments(assignmentsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      
      const errorMsg = error.response?.data?.error || error.message || 'Failed to load devices';
      setLoadError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (device) => {
    setDeviceToDelete(device);
    setConfirmationText('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (confirmationText !== 'DELETE' || !deviceToDelete) return;
    
    try {
      await deviceAPI.delete(deviceToDelete.assetId);
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
      setConfirmationText('');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete device');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeviceToDelete(null);
    setConfirmationText('');
  };

  const handleEditClick = (device) => {
    setDeviceToEdit(device);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setDeviceToEdit(null);
  };

  const handleEditSave = () => {
    loadData();
  };

  const handleViewClick = (device) => {
    setDeviceToView(device);
    setViewDialogOpen(true);
  };

  const handleViewClose = () => {
    setViewDialogOpen(false);
    setDeviceToView(null);
  };

  // Request device assignment to self (requires approval) - uses reassign dialog
  const handleAssignToSelf = (device) => {
    setDeviceToReassign(device);
    setSelectedAssignee(user.email); // Pre-select current user
    setReassignNotes('Self-assignment request');
    setReassignDialogOpen(true);
  };

  // Transfer device to another user (requires approval) - reuses reassign dialog
  const handleTransferDevice = (device) => {
    setDeviceToReassign(device);
    setSelectedAssignee('');
    setReassignNotes('');
    setReassignDialogOpen(true);
  };

  // Reassign device to another user (admin/manager - requires approval)
  const handleReassignDevice = (device) => {
    setDeviceToReassign(device);
    setSelectedAssignee('');
    setReassignNotes('');
    setReassignDialogOpen(true);
  };

  const handleReassignConfirm = async () => {
    if (!selectedAssignee || !deviceToReassign) return;
    
    setIsReassigning(true);
    try {
      await transferAPI.requestTransfer({
        deviceId: deviceToReassign.assetId,
        toEmployeeEmail: selectedAssignee,
        notes: reassignNotes || ''
      });
      alert('Assignment request sent successfully! The recipient will need to approve before the device is reassigned.');
      setReassignDialogOpen(false);
      setDeviceToReassign(null);
      setSelectedAssignee('');
      setReassignNotes('');
      loadData();
    } catch (error) {
      console.error('Error requesting reassignment:', error);
      alert(error.response?.data?.error || 'Failed to request reassignment');
    } finally {
      setIsReassigning(false);
    }
  };

  const handleReassignCancel = () => {
    setReassignDialogOpen(false);
    setDeviceToReassign(null);
    setSelectedAssignee('');
    setReassignNotes('');
  };

  // Unassign device (make it available) - admin/manager only
  const handleUnassignDevice = async (device) => {
    const assignment = assignments.find(
      a => a.deviceId === device.assetId && a.status === 'active'
    );
    
    if (!assignment) {
      alert('No active assignment found for this device');
      return;
    }

    const assignedUser = users.find(u => 
      (u.email && assignment.employeeId && u.email.toLowerCase() === assignment.employeeId.toLowerCase()) || 
      u.id === assignment.employeeId
    );
    const assigneeName = assignedUser ? (assignedUser.name || assignedUser.email) : 'Unknown User';

    if (window.confirm(`Are you sure you want to make this device available?\n\nDevice: ${device.assetId}\nCurrently assigned to: ${assigneeName}\n\nThis will remove the assignment and make the device available for others.`)) {
      try {
        await assignmentAPI.delete(assignment._id);
        alert('Device has been made available successfully!');
        loadData();
      } catch (error) {
        console.error('Error unassigning device:', error);
        alert(error.response?.data?.error || 'Failed to unassign device');
      }
    }
  };

  // Export devices to CSV
  const handleExportCSV = () => {
    try {
      // Define all fields to export
      const headers = [
        'Asset ID', 'Device Name', 'Device Type', 'Model', 'Delivery Unit',
        'OS Version', 'Color', 'RAM', 'Display Size', 'Storage', 'Chipset',
        'UDID', 'Serial Number', 'AR Support', 'Screen Resolution', 'Condition',
        'Status', 'Assignee Name', 'Description', 'Purchase Date', 'Purchased From', 'Purchase Status',
        'Registered Team ID', 'Usage Type', 'Created At', 'Updated At'
      ];

      // Create CSV content
      let csvContent = headers.join(',') + '\n';
      
      devices.forEach(device => {
        const row = [
          device.assetId || '',
          device.deviceName || '',
          device.deviceType || '',
          device.model || '',
          device.deliveryUnit || '',
          device.osVersion || '',
          device.color || '',
          device.ram || '',
          device.displaySize || '',
          device.storage || '',
          device.chipset || '',
          device.udid || '',
          device.serialNumber || '',
          device.arSupport || '',
          device.screenResolution || '',
          device.condition || '',
          device.status || '',
          device.assigneeName || '',
          device.description || '',
          device.purchaseDate || '',
          device.purchasedFrom || '',
          device.purchaseStatus || '',
          device.registeredTeamId || '',
          device.usageType || '',
          device.createdAt ? new Date(device.createdAt).toLocaleString() : '',
          device.updatedAt ? new Date(device.updatedAt).toLocaleString() : ''
        ].map(field => {
          // Escape fields containing commas, quotes, or newlines
          const stringField = String(field);
          if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
            return `"${stringField.replace(/"/g, '""')}"`;
          }
          return stringField;
        });
        
        csvContent += row.join(',') + '\n';
      });

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `devices_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setMenuOpen(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export devices. Please try again.');
    }
  };

  // Handle import dialog open
  const handleImportClick = () => {
    setImportDialogOpen(true);
    setMenuOpen(false);
    setImportFile(null);
    setImportErrors([]);
    setImportSuccess(false);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setImportFile(file);
      setImportErrors([]);
      setImportSuccess(false);
    } else {
      alert('Please select a valid CSV file.');
      e.target.value = null;
    }
  };

  // Parse CSV file
  const parseCSV = (text) => {
    const lines = text.split('\n');
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const devices = [];

    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;

      // Handle CSV parsing with quoted fields
      const values = [];
      let currentValue = '';
      let insideQuotes = false;

      for (let char of lines[i]) {
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          values.push(currentValue.trim().replace(/^"|"$/g, '').replace(/""/g, '"'));
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim().replace(/^"|"$/g, '').replace(/""/g, '"'));

      const device = {};
      headers.forEach((header, index) => {
        const fieldMap = {
          'Asset ID': 'assetId',
          'Device Name': 'deviceName',
          'Device Type': 'deviceType',
          'Model': 'model',
          'Delivery Unit': 'deliveryUnit',
          'OS Version': 'osVersion',
          'Color': 'color',
          'RAM': 'ram',
          'Display Size': 'displaySize',
          'Storage': 'storage',
          'Chipset': 'chipset',
          'UDID': 'udid',
          'Serial Number': 'serialNumber',
          'AR Support': 'arSupport',
          'Screen Resolution': 'screenResolution',
          'Condition': 'condition',
          'Status': 'status',
          'Description': 'description',
          'Purchase Date': 'purchaseDate',
          'Purchased From': 'purchasedFrom',
          'Purchase Status': 'purchaseStatus',
          'Registered Team ID': 'registeredTeamId',
          'Usage Type': 'usageType'
        };
        
        const fieldName = fieldMap[header];
        if (fieldName) {
          device[fieldName] = values[index] || '';
        }
      });

      if (device.assetId) {
        devices.push(device);
      }
    }

    return devices;
  };

  // Handle CSV import
  const handleImportCSV = async () => {
    if (!importFile) {
      alert('Please select a file first.');
      return;
    }

    setImporting(true);
    setImportErrors([]);
    setImportSuccess(false);

    try {
      const text = await importFile.text();
      const parsedDevices = parseCSV(text);

      if (parsedDevices.length === 0) {
        setImportErrors(['No valid devices found in the CSV file.']);
        setImporting(false);
        return;
      }

      // Validate required fields
      const errors = [];
      const validDevices = [];

      parsedDevices.forEach((device, index) => {
        const row = index + 2; // +2 because of header and 0-index
        
        if (!device.assetId || !device.assetId.trim()) {
          errors.push(`Row ${row}: Asset ID is required`);
          return;
        }
        if (!device.deviceName || !device.deviceName.trim()) {
          errors.push(`Row ${row}: Device Name is required`);
          return;
        }
        if (!device.deviceType || !device.deviceType.trim()) {
          errors.push(`Row ${row}: Device Type is required`);
          return;
        }
        if (!device.model || !device.model.trim()) {
          errors.push(`Row ${row}: Model is required`);
          return;
        }
        if (!device.condition || !device.condition.trim()) {
          errors.push(`Row ${row}: Condition is required`);
          return;
        }

        // Check for duplicate Asset IDs in existing devices
        const existingDevice = devices.find(d => d.assetId === device.assetId);
        if (existingDevice) {
          errors.push(`Row ${row}: Duplicate Asset ID "${device.assetId}" - already exists in database`);
          return;
        }

        // Check for duplicate Asset IDs within the import file
        const duplicateInImport = validDevices.find(d => d.assetId === device.assetId);
        if (duplicateInImport) {
          errors.push(`Row ${row}: Duplicate Asset ID "${device.assetId}" - found multiple times in import file`);
          return;
        }

        validDevices.push(device);
      });

      if (errors.length > 0) {
        setImportErrors(errors);
        setImporting(false);
        return;
      }

      // Send to backend for bulk import
      const response = await deviceAPI.bulkImport(validDevices);
      
      if (response.data.success) {
        setImportSuccess(true);
        
        // Check if there were partial errors during import
        if (response.data.errors && response.data.errors.length > 0) {
          setImportErrors([
            `✓ Successfully imported ${response.data.imported} out of ${response.data.total} devices.`,
            '',
            'The following errors occurred:',
            ...response.data.errors
          ]);
          // Don't auto-close if there were errors
        } else {
          setImportErrors([]);
          // Only auto-close on complete success
          setTimeout(() => {
            setImportDialogOpen(false);
            setImportFile(null);
          }, 2000);
        }
        
        await loadData(); // Reload devices
      } else {
        // Complete failure - no devices imported
        const backendErrors = response.data.errors || [];
        if (Array.isArray(backendErrors) && backendErrors.length > 0) {
          setImportErrors(backendErrors);
        } else {
          setImportErrors(['Import failed. Please try again.']);
        }
        setImportSuccess(false);
      }
    } catch (error) {
      console.error('Import error:', error);
      
      // Extract detailed error information
      let errorMessages = [];
      
      if (error.response?.data) {
        // Check for various error formats from backend
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          errorMessages = error.response.data.errors;
        } else if (error.response.data.error) {
          errorMessages = [error.response.data.error];
        } else if (error.response.data.message) {
          errorMessages = [error.response.data.message];
        } else if (typeof error.response.data === 'string') {
          errorMessages = [error.response.data];
        }
      }
      
      // If no specific errors found, use generic message
      if (errorMessages.length === 0) {
        if (error.message) {
          errorMessages = [`Import failed: ${error.message}`];
        } else {
          errorMessages = ['Failed to import devices. Please check your file and try again.'];
        }
      }
      
      setImportErrors(errorMessages);
    } finally {
      setImporting(false);
    }
  };

  // Download sample CSV template
  const handleDownloadTemplate = () => {
    try {
      const headers = [
        'Asset ID', 'Device Name', 'Device Type', 'Model', 'Delivery Unit',
        'OS Version', 'Color', 'RAM', 'Display Size', 'Storage', 'Chipset',
        'UDID', 'Serial Number', 'AR Support', 'Screen Resolution', 'Condition',
        'Status', 'Description', 'Purchase Date', 'Purchased From', 'Purchase Status',
        'Registered Team ID', 'Usage Type'
      ];

      // Sample data rows
      const sampleRows = [
        [
          'SAMPLE-001', 'iPhone 15 Pro', 'iOS', 'iPhone 15 Pro', 'DU1',
          'iOS 17.0', 'Natural Titanium', '8GB', '6.1 inches', '256GB', 'A17 Pro',
          '00000000-0000000001', 'ABC123456', 'Yes', '2556x1179', 'GOOD',
          'available', 'Sample device for testing import', '2024-01-15', 'Apple Store', 'New',
          'Team ID1 - U3X529X7BS', 'Development'
        ],
        [
          'SAMPLE-002', 'Samsung Galaxy S23', 'Android', 'Galaxy S23', 'DU2',
          'Android 14', 'Phantom Black', '8GB', '6.1 inches', '128GB', 'Snapdragon 8 Gen 2',
          '00000000-0000000002', 'XYZ789012', 'No', '2340x1080', 'GOOD',
          'available', 'Another sample device', '2024-02-20', 'Samsung Store', 'New',
          '', 'Testing'
        ]
      ];

      // Create CSV content
      let csvContent = headers.join(',') + '\n';
      
      sampleRows.forEach(row => {
        const formattedRow = row.map(field => {
          const stringField = String(field);
          if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
            return `"${stringField.replace(/"/g, '""')}"`;
          }
          return stringField;
        });
        csvContent += formattedRow.join(',') + '\n';
      });

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'device_import_template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template. Please try again.');
    }
  };

  const getAssignedEmployee = (assetId) => {
    const assignment = assignments.find(
      a => a.deviceId === assetId && a.status === 'active'
    );
    if (assignment) {
      const assignedUser = users.find(u => 
        (u.email && assignment.employeeId && u.email.toLowerCase() === assignment.employeeId.toLowerCase()) || 
        u.id === assignment.employeeId
      );
      return assignedUser ? (assignedUser.name || assignedUser.email) : 'Unknown';
    }
    return null;
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'available') return 'status-available';
    if (status === 'assigned') return 'status-assigned';
    return 'status-maintenance';
  };

  // Handle column filter change
  const handleFilterChange = (column, value) => {
    // Update filters with cascading logic - clear dependent filters when parent changes
    setColumnFilters(prev => {
      const newFilters = { ...prev, [column]: value };
      
      // If deliveryUnit changes, clear all dependent filters
      if (column === 'deliveryUnit') {
        newFilters.deviceType = '';
        newFilters.model = '';
        newFilters.osVersion = '';
        newFilters.condition = '';
        newFilters.status = '';
        newFilters.assignedTo = '';
      }
      
      // If deviceType changes, clear model and further dependent filters
      if (column === 'deviceType') {
        newFilters.model = '';
        newFilters.osVersion = '';
        newFilters.condition = '';
        newFilters.status = '';
        newFilters.assignedTo = '';
      }
      
      // If model changes, clear osVersion and further dependent filters
      if (column === 'model') {
        newFilters.osVersion = '';
        newFilters.condition = '';
        newFilters.status = '';
        newFilters.assignedTo = '';
      }
      
      return newFilters;
    });
    setDisplayedCount(20); // Reset displayed count when filter changes
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setDisplayedCount(20); // Reset displayed count when sorting
  };

  // Toggle search tooltip
  const toggleSearchTooltip = (column) => {
    if (activeSearchColumn === column) {
      setActiveSearchColumn(null);
    } else {
      setActiveSearchColumn(column);
    }
  };

  const handleSearchIconClick = (e, column) => {
    e.stopPropagation();
    toggleSearchTooltip(column);
  };

  const clearAllFilters = () => {
    setColumnFilters({
      assetId: '',
      deviceName: '',
      deviceType: '',
      model: '',
      osVersion: '',
      condition: '',
      status: '',
      assignedTo: '',
      deliveryUnit: ''
    });
    setCustomFilter({
      field: '',
      value: ''
    });
    setGlobalSearch('');
    setAssigneeFilter('');
    setShowAvailableOnly(false);
    setShowGoodOnly(true); // Default: Show only GOOD condition
    setActiveSearchColumn(null);
    setDisplayedCount(20); // Reset displayed count when clearing filters
    
    // Clear from localStorage and save defaults
    if (user?.email) {
      saveToStorage('columnFilters', {
        assetId: '',
        deviceName: '',
        deviceType: '',
        model: '',
        osVersion: '',
        condition: '',
        status: '',
        assignedTo: '',
        deliveryUnit: ''
      });
      saveToStorage('customFilter', { field: '', value: '' });
      saveToStorage('globalSearch', '');
      saveToStorage('assigneeFilter', '');
      saveToStorage('showAvailableOnly', false);
      saveToStorage('showGoodOnly', true); // Save default
    }
  };

  // Filter and sort devices
  const filteredAndSortedDevices = useMemo(() => {
    let filtered = devices.filter(device => {
      const assignedEmployee = getAssignedEmployee(device.assetId);
      
      // Apply global search (searches across multiple fields)
      let globalMatch = true;
      if (globalSearch) {
        const searchLower = globalSearch.toLowerCase();
        globalMatch = (
          (device.assetId || '').toLowerCase().includes(searchLower) ||
          (device.deviceName || '').toLowerCase().includes(searchLower) ||
          (device.model || '').toLowerCase().includes(searchLower) ||
          (device.osVersion || '').toLowerCase().includes(searchLower) ||
          (device.color || '').toLowerCase().includes(searchLower) ||
          (device.udid || '').toLowerCase().includes(searchLower) ||
          (device.description || '').toLowerCase().includes(searchLower)
        );
      }
      
      // Apply column filters
      const columnMatch = (
        (!columnFilters.assetId || (device.assetId || '').toLowerCase().includes(columnFilters.assetId.toLowerCase())) &&
        (!columnFilters.deviceName || (device.deviceName || '').toLowerCase().includes(columnFilters.deviceName.toLowerCase())) &&
        (!columnFilters.deviceType || (device.deviceType || '').toLowerCase() === columnFilters.deviceType.toLowerCase()) &&
        (!columnFilters.model || (device.model || '').toLowerCase() === columnFilters.model.toLowerCase()) &&
        (!columnFilters.osVersion || (device.osVersion || '').toLowerCase() === columnFilters.osVersion.toLowerCase()) &&
        (!columnFilters.condition || (device.condition || '').toLowerCase() === columnFilters.condition.toLowerCase()) &&
        (!columnFilters.status || (device.status || '').toLowerCase() === columnFilters.status.toLowerCase()) &&
        (!columnFilters.assignedTo || (assignedEmployee || '').toLowerCase().includes(columnFilters.assignedTo.toLowerCase())) &&
        (!columnFilters.deliveryUnit || (device.deliveryUnit || '').toLowerCase() === columnFilters.deliveryUnit.toLowerCase())
      );

      // Apply assignee filter
      let assigneeMatch = true;
      if (assigneeFilter) {
        if (assigneeFilter === '__ASSIGNED_TO_ME__') {
          assigneeMatch = (device.assigneeName || '') === (user?.name || '');
        } else {
          assigneeMatch = (device.assigneeName || '').toLowerCase() === assigneeFilter.toLowerCase();
        }
      }

      // Apply custom filter
      let customMatch = true;
      if (customFilter.field && customFilter.value) {
        // Special handling for "No Team ID" option
        if (customFilter.field === 'registeredTeamId' && customFilter.value === '(No Team ID)') {
          customMatch = !device.registeredTeamId || device.registeredTeamId.trim() === '';
        } else {
          const fieldValue = (device[customFilter.field] || '').toLowerCase();
          customMatch = fieldValue === customFilter.value.toLowerCase();
        }
      }

      // Apply quick filters for status and condition
      let statusMatch = true;
      if (showAvailableOnly) {
        statusMatch = (device.status || '').toLowerCase() === 'available';
      }

      let conditionMatch = true;
      if (showGoodOnly) {
        conditionMatch = (device.condition || '').toLowerCase() === 'good';
      }

      return globalMatch && columnMatch && customMatch && assigneeMatch && statusMatch && conditionMatch;
    });

    // Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (sortColumn === 'assignedTo') {
          aValue = getAssignedEmployee(a.assetId) || '';
          bValue = getAssignedEmployee(b.assetId) || '';
        } else {
          aValue = a[sortColumn] || '';
          bValue = b[sortColumn] || '';
        }
        
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
        
        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [devices, columnFilters, sortColumn, sortDirection, assignments, users, customFilter, globalSearch, assigneeFilter, showAvailableOnly, showGoodOnly, user]);

  // Infinite scroll - get displayed devices
  const displayedDevices = useMemo(() => {
    return filteredAndSortedDevices.slice(0, displayedCount);
  }, [filteredAndSortedDevices, displayedCount]);

  const hasMore = displayedCount < filteredAndSortedDevices.length;

  const getSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return '⇅';
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(columnFilters).some(value => value !== '') || (customFilter.field && customFilter.value) || globalSearch || assigneeFilter || showAvailableOnly;
  }, [columnFilters, customFilter, globalSearch, assigneeFilter, showAvailableOnly]);

  // Get unique device types and models for dropdowns
  // Cascading filter options - each filter is based on previous filter selections
  const uniqueDeliveryUnits = useMemo(() => {
    const units = [...new Set(devices.map(d => d.deliveryUnit).filter(Boolean))].sort();
    console.log('DeviceList - Unique Delivery Units:', units);
    return units;
  }, [devices]);

  const uniqueDeviceTypes = useMemo(() => {
    let filteredDevices = devices;
    
    // Filter by delivery unit if selected
    if (columnFilters.deliveryUnit) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deliveryUnit || '').toLowerCase() === columnFilters.deliveryUnit.toLowerCase()
      );
    }
    
    const types = [...new Set(filteredDevices.map(d => d.deviceType).filter(Boolean))].sort();
    console.log('DeviceList - Unique Device Types:', types);
    return types;
  }, [devices, columnFilters.deliveryUnit]);

  const uniqueModels = useMemo(() => {
    let filteredDevices = devices;
    
    // Filter by delivery unit if selected
    if (columnFilters.deliveryUnit) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deliveryUnit || '').toLowerCase() === columnFilters.deliveryUnit.toLowerCase()
      );
    }
    
    // Filter by device type if selected
    if (columnFilters.deviceType) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deviceType || '').toLowerCase() === columnFilters.deviceType.toLowerCase()
      );
    }
    
    const models = [...new Set(filteredDevices.map(d => d.model).filter(Boolean))].sort();
    console.log('DeviceList - Unique Models:', models);
    return models;
  }, [devices, columnFilters.deliveryUnit, columnFilters.deviceType]);

  const uniqueOsVersions = useMemo(() => {
    let filteredDevices = devices;
    
    // Filter by delivery unit if selected
    if (columnFilters.deliveryUnit) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deliveryUnit || '').toLowerCase() === columnFilters.deliveryUnit.toLowerCase()
      );
    }
    
    // Filter by device type if selected
    if (columnFilters.deviceType) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deviceType || '').toLowerCase() === columnFilters.deviceType.toLowerCase()
      );
    }
    
    // Filter by model if selected
    if (columnFilters.model) {
      filteredDevices = filteredDevices.filter(d => 
        (d.model || '').toLowerCase().includes(columnFilters.model.toLowerCase())
      );
    }
    
    const versions = [...new Set(filteredDevices.map(d => d.osVersion).filter(Boolean))].sort();
    console.log('DeviceList - Unique OS Versions:', versions);
    return versions;
  }, [devices, columnFilters.deliveryUnit, columnFilters.deviceType, columnFilters.model]);

  const uniqueConditions = useMemo(() => {
    let filteredDevices = devices;
    
    // Filter by delivery unit if selected
    if (columnFilters.deliveryUnit) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deliveryUnit || '').toLowerCase() === columnFilters.deliveryUnit.toLowerCase()
      );
    }
    
    // Filter by device type if selected
    if (columnFilters.deviceType) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deviceType || '').toLowerCase() === columnFilters.deviceType.toLowerCase()
      );
    }
    
    // Filter by model if selected
    if (columnFilters.model) {
      filteredDevices = filteredDevices.filter(d => 
        (d.model || '').toLowerCase().includes(columnFilters.model.toLowerCase())
      );
    }
    
    // Filter by OS version if selected
    if (columnFilters.osVersion) {
      filteredDevices = filteredDevices.filter(d => 
        (d.osVersion || '').toLowerCase().includes(columnFilters.osVersion.toLowerCase())
      );
    }
    
    const conditions = [...new Set(filteredDevices.map(d => d.condition).filter(Boolean))].sort();
    console.log('DeviceList - Unique Conditions:', conditions);
    return conditions;
  }, [devices, columnFilters.deliveryUnit, columnFilters.deviceType, columnFilters.model, columnFilters.osVersion]);

  const uniqueStatuses = useMemo(() => {
    let filteredDevices = devices;
    
    // Filter by delivery unit if selected
    if (columnFilters.deliveryUnit) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deliveryUnit || '').toLowerCase() === columnFilters.deliveryUnit.toLowerCase()
      );
    }
    
    // Filter by device type if selected
    if (columnFilters.deviceType) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deviceType || '').toLowerCase() === columnFilters.deviceType.toLowerCase()
      );
    }
    
    // Filter by model if selected
    if (columnFilters.model) {
      filteredDevices = filteredDevices.filter(d => 
        (d.model || '').toLowerCase().includes(columnFilters.model.toLowerCase())
      );
    }
    
    const statuses = [...new Set(filteredDevices.map(d => d.status).filter(Boolean))].sort();
    console.log('DeviceList - Unique Statuses:', statuses);
    return statuses;
  }, [devices, columnFilters.deliveryUnit, columnFilters.deviceType, columnFilters.model]);

  const uniqueAssignees = useMemo(() => {
    let filteredDevices = devices;
    
    // Filter by delivery unit if selected
    if (columnFilters.deliveryUnit) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deliveryUnit || '').toLowerCase() === columnFilters.deliveryUnit.toLowerCase()
      );
    }
    
    // Filter by device type if selected
    if (columnFilters.deviceType) {
      filteredDevices = filteredDevices.filter(d => 
        (d.deviceType || '').toLowerCase() === columnFilters.deviceType.toLowerCase()
      );
    }
    
    // Filter by model if selected
    if (columnFilters.model) {
      filteredDevices = filteredDevices.filter(d => 
        (d.model || '').toLowerCase().includes(columnFilters.model.toLowerCase())
      );
    }
    
    const assignees = [...new Set(filteredDevices.map(d => d.assigneeName).filter(Boolean))].sort();
    return assignees;
  }, [devices, columnFilters.deliveryUnit, columnFilters.deviceType, columnFilters.model]);

  // Filter available users for reassignment based on role
  const availableUsersForReassignment = useMemo(() => {
    if (!users || users.length === 0) return [];
    
    // Filter active users only
    let filteredUsers = users.filter(u => u.isActive);
    
    // For employees: only show users from their delivery unit
    if (user?.role === 'employee' && user?.deliveryUnit) {
      filteredUsers = filteredUsers.filter(u => u.deliveryUnit === user.deliveryUnit);
    }
    // For admins and managers: show all users (no additional filtering needed)
    
    return filteredUsers.sort((a, b) => {
      const nameA = (a.name || a.email || '').toLowerCase();
      const nameB = (b.name || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [users, user]);

  // Get unique values for custom filter based on selected field
  const getUniqueValuesForField = (fieldName) => {
    if (!fieldName) return [];
    const values = [...new Set(devices.map(d => d[fieldName]).filter(Boolean))].sort();
    
    // Add special option for empty/unassigned Team IDs
    if (fieldName === 'registeredTeamId') {
      const hasEmptyTeamId = devices.some(d => !d[fieldName]);
      if (hasEmptyTeamId) {
        values.unshift('(No Team ID)');
      }
    }
    
    return values;
  };

  const customFilterOptions = useMemo(() => {
    return getUniqueValuesForField(customFilter.field);
  }, [devices, customFilter.field]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeSearchColumn && !e.target.closest('.search-tooltip') && !e.target.closest('.search-icon')) {
        setActiveSearchColumn(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeSearchColumn]);

  // Infinite scroll handler using Intersection Observer with scroll fallback
  useEffect(() => {
    if (!hasMore) return;

    let observer = null;
    let timeoutId = null;
    let scrollHandler = null;

    const loadMore = () => {
      if (isLoadingMore || !hasMore) return;
      setIsLoadingMore(true);
      // Simulate slight delay for smooth UX
      setTimeout(() => {
        setDisplayedCount(prev => Math.min(prev + itemsPerPage, filteredAndSortedDevices.length));
        setIsLoadingMore(false);
      }, 300);
    };

    // Use a small delay to ensure DOM is ready
    timeoutId = setTimeout(() => {
      // Find the table container (root for scrolling)
      const tableContainer = document.querySelector('.table-container');
      if (!tableContainer) {
        return;
      }

      // Find the sentinel element
      const sentinel = tableContainer.querySelector('.infinite-scroll-sentinel');
      if (!sentinel) {
        return;
      }

      // Check if table container is scrollable
      const isScrollable = tableContainer.scrollHeight > tableContainer.clientHeight;
      const rootElement = isScrollable ? tableContainer : null;
      const scrollContainer = isScrollable ? tableContainer : window;

      // Create a sentinel element observer
      observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasMore && !isLoadingMore) {
            loadMore();
          }
        },
        {
          root: rootElement, // Use table container if scrollable, otherwise viewport
          rootMargin: '100px', // Start loading 100px before reaching the sentinel
          threshold: 0.1
        }
      );

      observer.observe(sentinel);

      // Fallback: Also listen to scroll events
      scrollHandler = () => {
        if (isLoadingMore || !hasMore) return;
        
        let scrollTop, scrollHeight, clientHeight;
        if (isScrollable) {
          scrollTop = tableContainer.scrollTop;
          scrollHeight = tableContainer.scrollHeight;
          clientHeight = tableContainer.clientHeight;
        } else {
          scrollTop = window.scrollY || document.documentElement.scrollTop;
          scrollHeight = document.documentElement.scrollHeight;
          clientHeight = window.innerHeight;
        }

        // Load more when within 200px of bottom
        const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
        if (distanceFromBottom < 200) {
          loadMore();
        }
      };

      scrollContainer.addEventListener('scroll', scrollHandler, { passive: true });
    }, 100);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (observer) {
        observer.disconnect();
      }
      if (scrollHandler) {
        const tableContainer = document.querySelector('.table-container');
        const isScrollable = tableContainer && tableContainer.scrollHeight > tableContainer.clientHeight;
        const scrollContainer = isScrollable ? tableContainer : window;
        scrollContainer.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [hasMore, isLoadingMore, filteredAndSortedDevices.length, itemsPerPage, displayedCount]);

  if (loading) {
    return (
      <div className="card">
        <p>Loading devices...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="card">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}>⚠️</div>
          <h3 style={{ color: '#ef4444', marginBottom: '0.5rem' }}>Error Loading Devices</h3>
          <p style={{ color: '#64748b', marginBottom: '1rem' }}>{loadError}</p>
          <button
            onClick={loadData}
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="device-list">
      <div className="table-toolbar">
        <div className="toolbar-title">
          <div className="toolbar-count" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#1e293b',
            padding: '8px 0'
          }}>
            <span style={{
              color: '#003E97',
              fontWeight: '700'
            }}>
              📱 Devices Overview
            </span>
            <span style={{ 
              marginLeft: '12px',
              fontSize: '14px',
              color: '#64748b',
              fontWeight: '500'
            }}>
              {displayedDevices.length} of {filteredAndSortedDevices.length} {filteredAndSortedDevices.length === 1 ? 'device' : 'devices'}
              {filteredAndSortedDevices.length !== devices.length && ` (${devices.length} total)`}
            </span>
          </div>
        </div>
      </div>

      {/* Compact Filters Bar with Gradient Theme */}
      <div className="filters-bar" style={{
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #003E97 0%, #1a5bb8 50%, #4A8FD8 100%)',
        borderRadius: '8px',
        marginBottom: '12px',
        boxShadow: '0 4px 6px -1px rgba(0, 62, 151, 0.2), 0 2px 4px -1px rgba(0, 62, 151, 0.15)'
      }}>
        {/* All Filters in One Row */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          {/* Global Search - Compact */}
          <div style={{ minWidth: '300px' }}>
            <input
              type="text"
              placeholder="🔍 Search (Asset ID, Model, OS, UDID...)"
              value={globalSearch}
              onChange={(e) => {
                setGlobalSearch(e.target.value);
                setDisplayedCount(20);
              }}
              style={{
                width: '100%',
                padding: '7px 12px',
                fontSize: '13px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '6px',
                backgroundColor: globalSearch ? '#fef3c7' : 'white',
                outline: 'none',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                fontWeight: '500'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#fbbf24';
                e.target.style.boxShadow = '0 0 0 3px rgba(251, 191, 36, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
              }}
            />
          </div>

          {/* Delivery Unit Filter */}
          <div style={{ minWidth: '150px' }}>
            <select
              value={columnFilters.deliveryUnit}
              onChange={(e) => handleFilterChange('deliveryUnit', e.target.value)}
              style={{
                width: '100%',
                padding: '7px 10px',
                fontSize: '13px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '6px',
                backgroundColor: columnFilters.deliveryUnit ? '#a78bfa' : 'white',
                color: columnFilters.deliveryUnit ? 'white' : '#1e293b',
                fontWeight: '500',
                cursor: 'pointer',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                outline: 'none'
              }}
            >
              <option value="">All Units</option>
              {uniqueDeliveryUnits.length === 0 ? (
                <option disabled>No delivery units available</option>
              ) : (
                uniqueDeliveryUnits.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))
              )}
            </select>
          </div>

          {/* Device Type Filter */}
          <div style={{ minWidth: '150px' }}>
            <select
              value={columnFilters.deviceType}
              onChange={(e) => handleFilterChange('deviceType', e.target.value)}
              style={{
                width: '100%',
                padding: '7px 10px',
                fontSize: '13px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '6px',
                backgroundColor: columnFilters.deviceType ? '#a78bfa' : 'white',
                color: columnFilters.deviceType ? 'white' : '#1e293b',
                fontWeight: '500',
                cursor: 'pointer',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                outline: 'none'
              }}
            >
              <option value="">All Types</option>
              {uniqueDeviceTypes.length === 0 ? (
                <option disabled>No device types available</option>
              ) : (
                uniqueDeviceTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))
              )}
            </select>
          </div>

          {/* Quick Filter: Show Available Status - Toggle Slider */}
          <div className="toggle-slider-container">
            <span className="toggle-slider-label">Available</span>
            <div 
              className={`toggle-slider ${!showAvailableOnly ? 'inactive' : ''}`}
              onClick={() => {
                setShowAvailableOnly(!showAvailableOnly);
                setDisplayedCount(20);
              }}
              title="Filter devices with status 'Available'"
            >
              <div className="toggle-slider-thumb"></div>
            </div>
            <span className="toggle-slider-status">
              {showAvailableOnly ? 'ON' : 'OFF'}
            </span>
          </div>

          {/* Quick Filter: Show Good Condition - Toggle Slider */}
          <div className="toggle-slider-container">
            <span className="toggle-slider-label">Good</span>
            <div 
              className={`toggle-slider ${!showGoodOnly ? 'inactive' : ''}`}
              onClick={() => {
                setShowGoodOnly(!showGoodOnly);
                setDisplayedCount(20);
              }}
              title="Filter devices with condition 'Good'"
            >
              <div className="toggle-slider-thumb"></div>
            </div>
            <span className="toggle-slider-status">
              {showGoodOnly ? 'ON' : 'OFF'}
            </span>
          </div>

          {/* Filter Panel Button */}
          <button
            onClick={() => setFilterPanelOpen(true)}
            title="More Filters"
            style={{
              padding: '8px 12px',
              fontSize: '14px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '6px',
              backgroundColor: (columnFilters.model || customFilter.field) ? '#fef3c7' : 'white',
              color: (columnFilters.model || customFilter.field) ? '#92400e' : '#1e293b',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              marginLeft: 'auto'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 2px 4px 0 rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
            }}
          >
            <span>⚙️ More Filters</span>
            {(columnFilters.model || customFilter.field || assigneeFilter) && (
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '10px',
                fontWeight: '700'
              }}>
                {[columnFilters.model, customFilter.field, assigneeFilter].filter(Boolean).length}
              </span>
            )}
          </button>

          {/* Clear All Button */}
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              style={{
                padding: '7px 14px',
                fontSize: '13px',
                background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                transition: 'transform 0.1s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              ✕ Clear All
            </button>
          )}

          {/* Result Count */}
          {globalSearch && (
            <span style={{ 
              fontSize: '13px', 
              color: 'white',
              whiteSpace: 'nowrap',
              marginLeft: 'auto',
              fontWeight: '600',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              padding: '6px 12px',
              borderRadius: '6px'
            }}>
              {filteredAndSortedDevices.length} {filteredAndSortedDevices.length === 1 ? 'result' : 'results'}
            </span>
          )}

          {/* Three-dot menu for Export/Import */}
          <div style={{ position: 'relative', marginLeft: globalSearch ? '8px' : 'auto' }}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                padding: '7px 12px',
                fontSize: '18px',
                background: 'rgba(255, 255, 255, 0.95)',
                color: '#667eea',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                transition: 'transform 0.1s, background 0.2s',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.background = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.background = 'rgba(255, 255, 255, 0.95)';
              }}
              title="Export & Import Options"
            >
              ⋮
            </button>
            
            {menuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                zIndex: 1000,
                minWidth: '180px',
                overflow: 'hidden'
              }}>
                <button
                  onClick={handleExportCSV}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1e293b',
                    transition: 'background 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  📥 Export to CSV
                </button>
                
                {user?.role === 'super_admin' && (
                  <button
                    onClick={handleImportClick}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      borderTop: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#1e293b',
                      transition: 'background 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.target.style.background = 'none'}
                  >
                    📤 Import from CSV
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="devices-table">
          <thead>
            <tr className="column-heading-row">
              <th className="col-id sortable" onClick={() => handleSort('assetId')}>
                <div className="header-content">
                  <span className={`sort-icon ${sortColumn === 'assetId' ? 'active' : ''}`} title="Sort">
                    {getSortIcon('assetId')}
                  </span>
                  <span title="Asset ID">Asset ID</span>
                  <div className="header-actions">
                    <button
                      className={`search-icon ${columnFilters.assetId ? 'active' : ''}`}
                      onClick={(e) => handleSearchIconClick(e, 'assetId')}
                      title="Search Asset ID"
                    >
                      🔍
                    </button>
                  </div>
                  {activeSearchColumn === 'assetId' && (
                    <div className="search-tooltip" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        placeholder="Search Asset ID"
                        value={columnFilters.assetId}
                        onChange={(e) => handleFilterChange('assetId', e.target.value)}
                        className="tooltip-input"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('deviceName')}>
                <div className="header-content">
                  <span className={`sort-icon ${sortColumn === 'deviceName' ? 'active' : ''}`} title="Sort">
                    {getSortIcon('deviceName')}
                  </span>
                  <span title="Device Name">Device Name</span>
                  <div className="header-actions">
                    <button
                      className={`search-icon ${columnFilters.deviceName ? 'active' : ''}`}
                      onClick={(e) => handleSearchIconClick(e, 'deviceName')}
                      title="Search Device Name"
                    >
                      🔍
                    </button>
                  </div>
                  {activeSearchColumn === 'deviceName' && (
                    <div className="search-tooltip" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        placeholder="Search Device Name"
                        value={columnFilters.deviceName}
                        onChange={(e) => handleFilterChange('deviceName', e.target.value)}
                        className="tooltip-input"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('deviceType')}>
                <div className="header-content">
                  <span className={`sort-icon ${sortColumn === 'deviceType' ? 'active' : ''}`} title="Sort">
                    {getSortIcon('deviceType')}
                  </span>
                  <span title="Type">Type</span>
                  <div className="header-actions">
                    <button
                      className={`search-icon ${columnFilters.deviceType ? 'active' : ''}`}
                      onClick={(e) => handleSearchIconClick(e, 'deviceType')}
                      title="Search Type"
                    >
                      🔍
                    </button>
                  </div>
                  {activeSearchColumn === 'deviceType' && (
                    <div className="search-tooltip" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={columnFilters.deviceType}
                        onChange={(e) => handleFilterChange('deviceType', e.target.value)}
                        className="tooltip-input tooltip-select"
                        autoFocus
                      >
                        <option value="">All Types</option>
                        {uniqueDeviceTypes.length === 0 ? (
                          <option disabled>No device types found</option>
                        ) : (
                          uniqueDeviceTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('model')}>
                <div className="header-content">
                  <span className={`sort-icon ${sortColumn === 'model' ? 'active' : ''}`} title="Sort">
                    {getSortIcon('model')}
                  </span>
                  <span title="Model">Model</span>
                  <div className="header-actions">
                    <button
                      className={`search-icon ${columnFilters.model ? 'active' : ''}`}
                      onClick={(e) => handleSearchIconClick(e, 'model')}
                      title="Search Model"
                    >
                      🔍
                    </button>
                  </div>
                  {activeSearchColumn === 'model' && (
                    <div className="search-tooltip" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={columnFilters.model}
                        onChange={(e) => handleFilterChange('model', e.target.value)}
                        className="tooltip-input tooltip-select"
                        autoFocus
                      >
                        <option value="">All Models</option>
                        {uniqueModels.length === 0 ? (
                          <option disabled>No models found</option>
                        ) : (
                          uniqueModels.map(model => (
                            <option key={model} value={model}>{model}</option>
                          ))
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('deliveryUnit')}>
                <div className="header-content">
                  <span className={`sort-icon ${sortColumn === 'deliveryUnit' ? 'active' : ''}`} title="Sort">
                    {getSortIcon('deliveryUnit')}
                  </span>
                  <span title="Delivery Unit">Delivery Unit</span>
                  <div className="header-actions">
                    <button
                      className={`search-icon ${columnFilters.deliveryUnit ? 'active' : ''}`}
                      onClick={(e) => handleSearchIconClick(e, 'deliveryUnit')}
                      title="Search Delivery Unit"
                    >
                      🔍
                    </button>
                  </div>
                  {activeSearchColumn === 'deliveryUnit' && (
                    <div className="search-tooltip" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={columnFilters.deliveryUnit}
                        onChange={(e) => handleFilterChange('deliveryUnit', e.target.value)}
                        className="tooltip-input tooltip-select"
                        autoFocus
                      >
                        <option value="">All Units</option>
                        {uniqueDeliveryUnits.length === 0 ? (
                          <option disabled>No delivery units found</option>
                        ) : (
                          uniqueDeliveryUnits.map(unit => (
                            <option key={unit} value={unit}>{unit}</option>
                          ))
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('osVersion')}>
                <div className="header-content">
                  <span className={`sort-icon ${sortColumn === 'osVersion' ? 'active' : ''}`} title="Sort">
                    {getSortIcon('osVersion')}
                  </span>
                  <span title="OS">OS</span>
                  <div className="header-actions">
                    <button
                      className={`search-icon ${columnFilters.osVersion ? 'active' : ''}`}
                      onClick={(e) => handleSearchIconClick(e, 'osVersion')}
                      title="Search OS"
                    >
                      🔍
                    </button>
                  </div>
                  {activeSearchColumn === 'osVersion' && (
                    <div className="search-tooltip" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={columnFilters.osVersion}
                        onChange={(e) => handleFilterChange('osVersion', e.target.value)}
                        className="tooltip-input tooltip-select"
                        autoFocus
                      >
                        <option value="">All Versions</option>
                        {uniqueOsVersions.length === 0 ? (
                          <option disabled>No OS versions found</option>
                        ) : (
                          uniqueOsVersions.map(version => (
                            <option key={version} value={version}>{version}</option>
                          ))
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('condition')}>
                <div className="header-content">
                  <span className={`sort-icon ${sortColumn === 'condition' ? 'active' : ''}`} title="Sort">
                    {getSortIcon('condition')}
                  </span>
                  <span title="Condition">Condition</span>
                  <div className="header-actions">
                    <button
                      className={`search-icon ${columnFilters.condition ? 'active' : ''}`}
                      onClick={(e) => handleSearchIconClick(e, 'condition')}
                      title="Search Condition"
                    >
                      🔍
                    </button>
                  </div>
                  {activeSearchColumn === 'condition' && (
                    <div className="search-tooltip" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={columnFilters.condition}
                        onChange={(e) => handleFilterChange('condition', e.target.value)}
                        className="tooltip-input tooltip-select"
                        autoFocus
                      >
                        <option value="">All Conditions</option>
                        {uniqueConditions.length === 0 ? (
                          <option disabled>No conditions found</option>
                        ) : (
                          uniqueConditions.map(condition => (
                            <option key={condition} value={condition}>{condition}</option>
                          ))
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('status')}>
                <div className="header-content">
                  <span className={`sort-icon ${sortColumn === 'status' ? 'active' : ''}`} title="Sort">
                    {getSortIcon('status')}
                  </span>
                  <span title="Status">Status</span>
                  <div className="header-actions">
                    <button
                      className={`search-icon ${columnFilters.status ? 'active' : ''}`}
                      onClick={(e) => handleSearchIconClick(e, 'status')}
                      title="Search Status"
                    >
                      🔍
                    </button>
                  </div>
                  {activeSearchColumn === 'status' && (
                    <div className="search-tooltip" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={columnFilters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="tooltip-input tooltip-select"
                        autoFocus
                      >
                        <option value="">All Statuses</option>
                        {uniqueStatuses.length === 0 ? (
                          <option disabled>No statuses found</option>
                        ) : (
                          uniqueStatuses.map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('assignedTo')}>
                <div className="header-content">
                  <span className={`sort-icon ${sortColumn === 'assignedTo' ? 'active' : ''}`} title="Sort">
                    {getSortIcon('assignedTo')}
                  </span>
                  <span title="Assigned To">Assigned To</span>
                  <div className="header-actions">
                    <button
                      className={`search-icon ${columnFilters.assignedTo ? 'active' : ''}`}
                      onClick={(e) => handleSearchIconClick(e, 'assignedTo')}
                      title="Search Assigned To"
                    >
                      🔍
                    </button>
                  </div>
                  {activeSearchColumn === 'assignedTo' && (
                    <div className="search-tooltip" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        placeholder="Search Assigned To"
                        value={columnFilters.assignedTo}
                        onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
                        className="tooltip-input"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              </th>
              <th className="sortable col-updated" onClick={() => handleSort('updatedAt')}>
                <div className="header-content">
                  <span className={`sort-icon ${sortColumn === 'updatedAt' ? 'active' : ''}`} title="Sort">
                    {getSortIcon('updatedAt')}
                  </span>
                  <span title="Updated At">Updated At</span>
                </div>
              </th>
              <th className="col-actions">
                <div className="header-content">
                  <span title="Actions">Actions</span>
                </div>
              </th>
              <th className="col-clear-filters">
                <div className="header-content">
                  <button
                    onClick={clearAllFilters}
                    className={`btn-clear-filters-header ${!hasActiveFilters ? 'disabled' : ''}`}
                    title="Clear All Filters"
                    disabled={!hasActiveFilters}
                  >
                    🗑️
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayedDevices.length === 0 ? (
              <tr>
                <td colSpan="12" className="empty-table">
                  No devices found matching your criteria
                </td>
              </tr>
            ) : (
              displayedDevices.map(device => {
                const assignedEmployee = getAssignedEmployee(device.assetId);
                const isAssigned = !!assignedEmployee;
                return (
                  <tr 
                    key={device.assetId}
                    style={{ 
                      color: isAssigned ? '#9ca3af' : '#000000'
                    }}
                  >
                    <td className="device-id" style={{ color: isAssigned ? '#9ca3af' : '#000000' }}>
                      {device.assetId || 'N/A'}
                    </td>
                    <td className="device-name" style={{ color: isAssigned ? '#9ca3af' : '#000000' }}>
                      {device.deviceName || device.model || 'N/A'}
                    </td>
                    <td className="device-type" style={{ color: isAssigned ? '#9ca3af' : '#000000' }}>
                      {device.deviceType || device.type || 'N/A'}
                    </td>
                    <td className="device-model" style={{ color: isAssigned ? '#9ca3af' : '#000000' }}>
                      {device.model || 'N/A'}
                    </td>
                    <td className="device-delivery-unit" style={{ color: isAssigned ? '#9ca3af' : '#000000' }}>
                      {device.deliveryUnit || 'N/A'}
                    </td>
                    <td className="device-os" style={{ color: isAssigned ? '#9ca3af' : '#000000' }}>
                      {device.osVersion || 'N/A'}
                    </td>
                    <td className="device-condition">
                      <span 
                        className={`tag-badge ${device.condition === 'GOOD' ? 'tag-yes' : 'tag-no'}`}
                        style={{ 
                          color: isAssigned ? '#9ca3af' : undefined,
                          borderColor: isAssigned ? '#d1d5db' : undefined,
                          backgroundColor: isAssigned ? '#f3f4f6' : undefined
                        }}
                      >
                        {device.condition || 'N/A'}
                      </span>
                    </td>
                    <td className="device-status">
                      <span 
                        className={`tag-badge ${getStatusBadgeClass(device.status)}`}
                        style={{ 
                          color: isAssigned ? '#9ca3af' : undefined,
                          borderColor: isAssigned ? '#d1d5db' : undefined,
                          backgroundColor: isAssigned ? '#f3f4f6' : undefined
                        }}
                      >
                        {device.status || 'N/A'}
                      </span>
                    </td>
                    <td className="device-assigned">
                      {assignedEmployee ? (
                        <span 
                          className="tag-badge tag-assigned"
                          style={{ 
                            color: isAssigned ? '#9ca3af' : undefined,
                            borderColor: isAssigned ? '#d1d5db' : undefined,
                            backgroundColor: isAssigned ? '#f3f4f6' : undefined
                          }}
                        >
                          {assignedEmployee}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="device-updated" style={{ color: isAssigned ? '#9ca3af' : '#6b7280', fontSize: '0.8125rem' }}>
                      {device.updatedAt ? new Date(device.updatedAt).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}
                    </td>
                    <td className="device-actions">
                      <div className="actions-buttons-container">
                        {/* View button - Always visible */}
                        <button
                          className="btn-icon btn-view"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewClick(device);
                          }}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        
                        {/* For Employees: Show Request Assignment for available devices, Transfer for assigned devices */}
                        {user?.role === 'employee' && (
                          <>
                            {!assignedEmployee ? (
                              <button
                                className="btn-icon btn-request"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssignToSelf(device);
                                }}
                                title="Request Assignment"
                              >
                                <i className="fas fa-user-plus"></i>
                              </button>
                            ) : (
                              <button
                                className="btn-icon btn-transfer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (assignedEmployee === user?.name) {
                                    handleTransferDevice(device);
                                  }
                                }}
                                disabled={assignedEmployee !== user?.name}
                                title={
                                  assignedEmployee === user?.name
                                    ? "Request Transfer"
                                    : "Transfer only available for devices assigned to you"
                                }
                              >
                                <i className="fas fa-exchange-alt"></i>
                              </button>
                            )}
                          </>
                        )}
                        
                        {/* For non-employees: Show Transfer button (always disabled) */}
                        {user?.role !== 'employee' && (
                          <button
                            className="btn-icon btn-transfer"
                            disabled={true}
                            title="Transfer available for employees only"
                          >
                            <i className="fas fa-exchange-alt"></i>
                          </button>
                        )}
                        
                        {/* Edit button - available for all users */}
                        <button
                          className="btn-icon btn-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(device);
                          }}
                          title={user?.role === 'employee' ? 'Edit Device (Limited)' : 'Edit Device'}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        
                        {/* Admin/Manager Actions */}
                        {(user?.role === 'super_admin' || user?.role === 'device_manager') && (
                          <>
                            {/* Assignment Action button */}
                            <button
                              className="btn-icon btn-assignment-action"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReassignDevice(device);
                              }}
                              title={assignedEmployee ? "Reassign or Make Available" : "Assign Device"}
                            >
                              <i className="fas fa-user-cog"></i>
                            </button>
                            
                            {/* Delete button */}
                            <button
                              className="btn-icon btn-delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(device);
                              }}
                              title="Delete Device"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="col-clear-filters-cell"></td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        
        {/* Infinite Scroll Sentinel and Loading Indicator */}
        {hasMore && (
          <>
            <div className="infinite-scroll-sentinel"></div>
            {isLoadingMore && (
              <div className="infinite-scroll-loader">
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <span>Loading more devices...</span>
                </div>
              </div>
            )}
          </>
        )}

        {/* End of list message */}
        {!hasMore && displayedDevices.length > 0 && (
          <div className="end-of-list">
            <p>All devices loaded ({filteredAndSortedDevices.length} total)</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmationText={confirmationText}
        setConfirmationText={setConfirmationText}
        deviceName={deviceToDelete ? `${deviceToDelete.deviceName || deviceToDelete.assetId} (${deviceToDelete.assetId})` : ''}
      />

      {/* Edit Device Dialog */}
      <EditDeviceDialog
        isOpen={editDialogOpen}
        onClose={handleEditClose}
        device={deviceToEdit}
        onSave={handleEditSave}
        user={user}
      />

      {/* View Device Dialog */}
      {viewDialogOpen && deviceToView && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.2s ease'
          }}
          onClick={handleViewClose}
        >
          <div className="dialog-container" style={{ maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header" style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '20px',
              color: 'white',
              borderRadius: '8px 8px 0 0'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>📱 Device Details</h2>
              <p style={{ margin: '5px 0 0 0', fontSize: '13px', opacity: 0.9 }}>{deviceToView.assetId}</p>
            </div>
            <div className="dialog-body" style={{ padding: '20px', backgroundColor: '#f9fafb' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                {/* Basic Information */}
                <div style={{ gridColumn: '1 / -1', marginBottom: '10px' }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#667eea', borderBottom: '2px solid #667eea', paddingBottom: '8px' }}>Basic Information</h3>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Asset ID</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.assetId || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Device Name</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.deviceName || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Device Type</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.deviceType || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Model</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.model || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Delivery Unit</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.deliveryUnit || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Status</strong>
                  <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{deviceToView.status || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Condition</strong>
                  <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{deviceToView.condition || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Current Assignee</strong>
                  <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {getAssignedEmployee(deviceToView.assetId) || 'Not Assigned'}
                  </span>
                </div>

                {/* Technical Specifications */}
                <div style={{ gridColumn: '1 / -1', marginTop: '15px', marginBottom: '10px' }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#667eea', borderBottom: '2px solid #667eea', paddingBottom: '8px' }}>Technical Specifications</h3>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>OS Version</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.osVersion || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Chipset</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.chipset || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>RAM</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.ram || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Storage</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.storage || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Display Size</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.displaySize || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Screen Resolution</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.screenResolution || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Color</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.color || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>AR Support</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.arSupport || 'N/A'}</span>
                </div>

                {/* Identification */}
                <div style={{ gridColumn: '1 / -1', marginTop: '15px', marginBottom: '10px' }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#667eea', borderBottom: '2px solid #667eea', paddingBottom: '8px' }}>Identification</h3>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>UDID</strong>
                  <span style={{ fontSize: '14px', color: '#111827', wordBreak: 'break-all' }}>{deviceToView.udid || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Serial Number</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.serialNumber || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Registered Team ID</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.registeredTeamId || 'N/A'}</span>
                </div>

                {/* Purchase Information */}
                <div style={{ gridColumn: '1 / -1', marginTop: '15px', marginBottom: '10px' }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#667eea', borderBottom: '2px solid #667eea', paddingBottom: '8px' }}>Purchase Information</h3>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Purchase Date</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.purchaseDate || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Purchased From</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.purchasedFrom || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Purchase Status</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.purchaseStatus || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Usage Type</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.usageType || 'N/A'}</span>
                </div>

                {/* Additional Information */}
                <div style={{ gridColumn: '1 / -1', marginTop: '15px', marginBottom: '10px' }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#667eea', borderBottom: '2px solid #667eea', paddingBottom: '8px' }}>Additional Information</h3>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Description</strong>
                  <span style={{ fontSize: '14px', color: '#111827', whiteSpace: 'pre-wrap' }}>{deviceToView.description || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Created At</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.createdAt ? new Date(deviceToView.createdAt).toLocaleString() : 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Updated At</strong>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{deviceToView.updatedAt ? new Date(deviceToView.updatedAt).toLocaleString() : 'N/A'}</span>
                </div>
              </div>
            </div>
            <div className="dialog-footer" style={{ padding: '15px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleViewClose}
                style={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Panel - Right Side */}
      {filterPanelOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease'
          }}
          onClick={() => setFilterPanelOpen(false)}
        >
          <div 
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '400px',
              maxWidth: '90vw',
              backgroundColor: 'white',
              boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideInRight 0.3s ease',
              zIndex: 1001
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '24px',
              color: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>⚙️ More Filters</h2>
                <button
                  onClick={() => setFilterPanelOpen(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                >
                  ×
                </button>
              </div>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.95 }}>Additional filtering options</p>
            </div>
            
            {/* Body */}
            <div style={{ 
              padding: '24px', 
              backgroundColor: '#f9fafb',
              flex: 1,
              overflowY: 'auto'
            }}>
              {/* Model Filter */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#1e293b',
                  marginBottom: '10px'
                }}>
                  Model
                </label>
                <select
                  value={columnFilters.model}
                  onChange={(e) => handleFilterChange('model', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: columnFilters.model ? '#a78bfa' : 'white',
                    color: columnFilters.model ? 'white' : '#1e293b',
                    fontWeight: '500',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <option value="">All Models</option>
                  {uniqueModels.length === 0 ? (
                    <option disabled>No models available</option>
                  ) : (
                    uniqueModels.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))
                  )}
                </select>
              </div>

              {/* Assignee Filter */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#1e293b',
                  marginBottom: '10px'
                }}>
                  Assignee
                </label>
                <select
                  value={assigneeFilter}
                  onChange={(e) => {
                    setAssigneeFilter(e.target.value);
                    setDisplayedCount(20);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: assigneeFilter ? '#10b981' : 'white',
                    color: assigneeFilter ? 'white' : '#1e293b',
                    fontWeight: '500',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <option value="">All Assignees</option>
                  <option value="__ASSIGNED_TO_ME__">👤 Assigned to Me</option>
                  {uniqueAssignees.map(assignee => (
                    <option key={assignee} value={assignee}>{assignee}</option>
                  ))}
                </select>
              </div>

              {/* Divider */}
              <div style={{ borderBottom: '2px solid #e5e7eb', marginBottom: '20px' }}></div>

              {/* Custom Filter Section */}
              <h3 style={{ 
                fontSize: '16px', 
                fontWeight: '700', 
                color: '#1e293b',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🔧 Custom Filter
              </h3>

              {/* Custom Filter Field */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#1e293b',
                  marginBottom: '8px'
                }}>
                  Custom Field
                </label>
                <select
                  value={customFilter.field}
                  onChange={(e) => {
                    setCustomFilter({ field: e.target.value, value: '' });
                    setDisplayedCount(20);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: customFilter.field ? '#fbbf24' : 'white',
                    color: customFilter.field ? 'white' : '#1e293b',
                    fontWeight: '500',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <option value="">Select Field...</option>
                  <option value="condition">Condition</option>
                  <option value="status">Status</option>
                  <option value="osVersion">OS Version</option>
                  <option value="model">Model</option>
                  <option value="color">Color</option>
                  <option value="ram">RAM</option>
                  <option value="storage">Storage</option>
                  <option value="chipset">Chipset</option>
                  <option value="arSupport">AR Support</option>
                  <option value="purchaseStatus">Purchase Status</option>
                  <option value="usageType">Usage Type</option>
                  <option value="registeredTeamId">Registered Team ID</option>
                </select>
              </div>

              {/* Custom Filter Value */}
              {customFilter.field && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#1e293b',
                    marginBottom: '8px'
                  }}>
                    Custom Value
                  </label>
                  <select
                    value={customFilter.value}
                    onChange={(e) => {
                      setCustomFilter(prev => ({ ...prev, value: e.target.value }));
                      setDisplayedCount(20);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      fontSize: '14px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: customFilter.value ? '#fbbf24' : 'white',
                      color: customFilter.value ? 'white' : '#1e293b',
                      fontWeight: '500',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <option value="">Select Value...</option>
                    {customFilterOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Active Filters Info */}
              {(columnFilters.model || customFilter.field) && (
                <div style={{
                  marginTop: '20px',
                  padding: '14px',
                  background: '#dbeafe',
                  border: '1px solid #3b82f6',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#1e40af',
                  fontWeight: '500'
                }}>
                  ✓ {[columnFilters.model && 'Model', customFilter.field && 'Custom'].filter(Boolean).join(', ')} filter{[columnFilters.model, customFilter.field].filter(Boolean).length > 1 ? 's' : ''} active
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div style={{ 
              padding: '20px 24px', 
              borderTop: '2px solid #e5e7eb', 
              display: 'flex', 
              gap: '12px',
              backgroundColor: 'white',
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)',
              flexShrink: 0
            }}>
              <button
                onClick={() => {
                  // Reset all filters to default state
                  setColumnFilters({
                    assetId: '',
                    deviceName: '',
                    deviceType: '',
                    model: '',
                    osVersion: '',
                    condition: '',
                    status: '',
                    assignedTo: '',
                    deliveryUnit: ''
                  });
                  setCustomFilter({ field: '', value: '' });
                  setGlobalSearch('');
                  setAssigneeFilter('');
                  setShowAvailableOnly(false);
                  setShowGoodOnly(true); // Default: Show only GOOD condition
                  setDisplayedCount(20);
                  
                  // Save defaults to localStorage
                  saveToStorage('columnFilters', {
                    assetId: '',
                    deviceName: '',
                    deviceType: '',
                    model: '',
                    osVersion: '',
                    condition: '',
                    status: '',
                    assignedTo: '',
                    deliveryUnit: ''
                  });
                  saveToStorage('customFilter', { field: '', value: '' });
                  saveToStorage('globalSearch', '');
                  saveToStorage('assigneeFilter', '');
                  saveToStorage('showAvailableOnly', false);
                  saveToStorage('showGoodOnly', true);
                }}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  fontSize: '14px',
                  background: '#f3f4f6',
                  color: '#1e293b',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
              >
                Clear Filters
              </button>
              <button
                onClick={() => setFilterPanelOpen(false)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  fontSize: '14px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                }}
              >
                Apply & Close
              </button>
            </div>
          </div>

          {/* Add animations */}
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
              }
              to {
                transform: translateX(0);
              }
            }
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}

      {/* Import CSV Dialog */}
      {importDialogOpen && (
        <div className="dialog-overlay" onClick={() => !importing && setImportDialogOpen(false)}>
          <div className="dialog-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header" style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '20px',
              color: 'white',
              borderRadius: '8px 8px 0 0'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>📤 Import Devices from CSV</h2>
              <p style={{ margin: '5px 0 0 0', fontSize: '13px', opacity: 0.9 }}>Upload a CSV file to bulk import devices</p>
            </div>
            
            <div className="dialog-body" style={{ padding: '20px', backgroundColor: '#f9fafb' }}>
              {importSuccess && importErrors.length === 0 ? (
                <div style={{
                  padding: '20px',
                  background: '#d1fae5',
                  border: '2px solid #10b981',
                  borderRadius: '8px',
                  color: '#065f46',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  ✓ Devices imported successfully!
                </div>
              ) : (
                <>
                  {/* Show partial success banner if some devices were imported but had errors */}
                  {importSuccess && importErrors.length > 0 && (
                    <div style={{
                      padding: '15px',
                      background: '#fef3c7',
                      border: '2px solid #f59e0b',
                      borderRadius: '8px',
                      color: '#92400e',
                      fontWeight: '600',
                      textAlign: 'center',
                      marginBottom: '20px'
                    }}>
                      ⚠️ Partial Import Complete - Some devices were not imported due to errors
                    </div>
                  )}
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#1e293b',
                      marginBottom: '8px'
                    }}>
                      Select CSV File
                    </label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      disabled={importing}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        cursor: importing ? 'not-allowed' : 'pointer'
                      }}
                    />
                  </div>

                  {/* Download Template Button */}
                  <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <button
                      onClick={handleDownloadTemplate}
                      disabled={importing}
                      style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: importing ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        opacity: importing ? 0.5 : 1,
                        transition: 'transform 0.1s'
                      }}
                      onMouseEnter={(e) => !importing && (e.target.style.transform = 'scale(1.02)')}
                      onMouseLeave={(e) => !importing && (e.target.style.transform = 'scale(1)')}
                    >
                      📥 Download Sample CSV Template
                    </button>
                  </div>

                  <div style={{
                    padding: '15px',
                    background: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#92400e',
                    marginBottom: '15px'
                  }}>
                    <strong>Required Fields:</strong> Asset ID, Device Name, Device Type, Model, Condition
                    <br />
                    <strong>Note:</strong> Duplicate Asset IDs will be rejected
                  </div>

                  {importErrors.length > 0 && (
                    <div style={{
                      maxHeight: '250px',
                      overflowY: 'auto',
                      padding: '15px',
                      background: '#fee2e2',
                      border: '2px solid #ef4444',
                      borderRadius: '8px',
                      marginBottom: '15px'
                    }}>
                      <strong style={{ color: '#991b1b', display: 'block', marginBottom: '10px', fontSize: '14px' }}>
                        ⚠️ Import Failed - {importErrors.length} Error{importErrors.length > 1 ? 's' : ''} Found:
                      </strong>
                      <ul style={{ margin: 0, paddingLeft: '20px', color: '#991b1b' }}>
                        {importErrors.map((error, index) => (
                          <li key={index} style={{ marginBottom: '8px', wordBreak: 'break-word', lineHeight: '1.5' }}>
                            {error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {importing && (
                    <div style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#667eea',
                      fontWeight: '600'
                    }}>
                      ⏳ Importing devices...
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="dialog-footer" style={{ padding: '15px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => {
                  setImportDialogOpen(false);
                  setImportErrors([]);
                  setImportSuccess(false);
                  setImportFile(null);
                }}
                disabled={importing}
                style={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  background: '#f3f4f6',
                  color: '#1e293b',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: importing ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  opacity: importing ? 0.5 : 1
                }}
              >
                {importSuccess || importErrors.length > 0 ? 'Close' : 'Cancel'}
              </button>
              {!importSuccess && (
                <button
                  onClick={handleImportCSV}
                  disabled={!importFile || importing}
                  style={{
                    padding: '8px 20px',
                    fontSize: '14px',
                    background: (!importFile || importing) ? '#d1d5db' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (!importFile || importing) ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    boxShadow: (!importFile || importing) ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  {importing ? 'Importing...' : 'Import'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reassign Device Dialog */}
      {reassignDialogOpen && deviceToReassign && (
        <div className="dialog-overlay" onClick={handleReassignCancel}>
          <div className="dialog-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header" style={{ 
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              padding: '20px',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {user?.role === 'employee' ? '🔄 Request Device Transfer' : '👤 Reassign Device'}
              </h2>
              <p style={{ margin: '5px 0 0 0', fontSize: '13px', opacity: 0.9 }}>
                {deviceToReassign.deviceName || deviceToReassign.assetId}
              </p>
            </div>
            
            <div className="dialog-body" style={{ padding: '0 20px 20px' }}>
              {/* Current Assignee Info */}
              {deviceToReassign.assigneeName && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f3f4f6', 
                  borderRadius: '6px', 
                  marginBottom: '20px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Current Assignee</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {deviceToReassign.assigneeName}
                  </div>
                </div>
              )}

              {/* User Selection */}
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="assignee-select" style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Select New Assignee *
                  {user?.role === 'employee' && (
                    <span style={{ fontSize: '12px', fontWeight: '400', color: '#6b7280', marginLeft: '8px' }}>
                      (Users from your delivery unit)
                    </span>
                  )}
                </label>
                <select
                  id="assignee-select"
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">-- Select a user --</option>
                  {availableUsersForReassignment.map(u => (
                    <option key={u.email} value={u.email}>
                      {u.name || u.email} ({u.email})
                      {u.deliveryUnit ? ` - ${u.deliveryUnit}` : ''}
                    </option>
                  ))}
                </select>
                {availableUsersForReassignment.length === 0 && (
                  <div style={{ 
                    marginTop: '8px', 
                    fontSize: '13px', 
                    color: '#dc2626',
                    fontStyle: 'italic'
                  }}>
                    No available users found
                    {user?.role === 'employee' && ' in your delivery unit'}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="reassign-notes" style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Notes (Optional)
                </label>
                <textarea
                  id="reassign-notes"
                  value={reassignNotes}
                  onChange={(e) => setReassignNotes(e.target.value)}
                  placeholder="Add any notes about this assignment..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {/* Info Message */}
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '6px', 
                marginBottom: '20px',
                border: '1px solid #fbbf24'
              }}>
                <div style={{ fontSize: '13px', color: '#92400e', display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '8px', fontSize: '16px' }}>ℹ️</span>
                  <span>
                    The selected user will receive an assignment request in their "Assignment Requests" menu. 
                    They must approve it before the device is {deviceToReassign.assigneeName ? 'reassigned' : 'assigned'}.
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* Left side - Make Available button (only if device is currently assigned) */}
                {deviceToReassign.assigneeName && (
                  <button
                    onClick={() => {
                      handleUnassignDevice(deviceToReassign);
                      handleReassignCancel();
                    }}
                    disabled={isReassigning}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      background: isReassigning ? '#d1d5db' : '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isReassigning ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      boxShadow: isReassigning ? 'none' : '0 2px 4px rgba(239, 68, 68, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <i className="fas fa-user-times"></i>
                    <span>Make Available</span>
                  </button>
                )}
                
                {/* Right side - Cancel and Send Request buttons */}
                <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
                  <button
                    onClick={handleReassignCancel}
                    disabled={isReassigning}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      background: '#f3f4f6',
                      color: '#1e293b',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isReassigning ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      opacity: isReassigning ? 0.5 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReassignConfirm}
                    disabled={!selectedAssignee || isReassigning}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      background: (!selectedAssignee || isReassigning) ? '#d1d5db' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: (!selectedAssignee || isReassigning) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      boxShadow: (!selectedAssignee || isReassigning) ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.2)'
                    }}
                  >
                    {isReassigning ? 'Sending Request...' : 'Send Assignment Request'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceList;
